import { BlockEmbed } from 'quill/blots/block';
import type { HTMLAttributes } from 'react';
import { createElement, useRef } from 'react';
import { useQuill, useQuillReadOnly } from '../QuillContext';

// In Chrome, triple-click causes the focus node of the selection to be the parent element
// of the editor in read mode, which makes `Quill#getSelection()` return `null`.
// This function simulates `user-select: contain` to ensure that the selection is
// either outside the editor or inside the editor, preventing a situation
// where a part of it is inside the editor and a part of it is outside the editor.
export const containSelection = (container: HTMLElement) => {
  // Inspired by
  // https://github.com/github/user-select-contain-polyfill/blob/main/user-select-contain.js#L17
  const selection = window.getSelection();
  if (!selection?.rangeCount || selection.type === 'Caret') return;

  const range = selection.getRangeAt(0);
  const isStartingInsideContainer = container.contains(range.startContainer);
  const isEndingInsideContainer = container.contains(range.endContainer);

  if (
    isStartingInsideContainer &&
    !isEndingInsideContainer &&
    container.lastChild
  ) {
    range.setEndAfter(container.lastChild);
  } else if (
    !isStartingInsideContainer &&
    isEndingInsideContainer &&
    container.firstChild
  ) {
    range.setStartBefore(container.firstChild);
  }
};

interface UserSelectContainerProps<T extends keyof HTMLElementTagNameMap>
  extends Omit<HTMLAttributes<HTMLElementTagNameMap[T]>, 'onClick' | 'ref'> {
  as: T;
}

const UserSelectContainer = <T extends keyof HTMLElementTagNameMap = 'div'>({
  as,
  ...props
}: UserSelectContainerProps<T>) => {
  const { quill } = useQuill();
  const readOnly = useQuillReadOnly();
  const ref = useRef<HTMLElementTagNameMap[T] | null>(null);

  return createElement(as, {
    ...props,
    ref,
    onClick: (event) => {
      if (!quill) return;

      // contenteditable="true" behaves like `user-select: contain` by default
      if (!readOnly) return;

      // Skip if the event is fired from inside a React portal.
      if (
        !(event.target instanceof Element) ||
        !ref.current?.contains(event.target)
      ) {
        return;
      }

      const blot = quill.scroll.find(event.target, true);
      if (blot instanceof BlockEmbed) {
        return;
      }

      containSelection(quill.root);
    },
  });
};

export default UserSelectContainer;
